import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay, faXmark } from '@fortawesome/free-solid-svg-icons';

import { ToolbarButton } from '../ToolbarButton';

export interface ProjectPlaybackActionsProps {
  isPlaying: boolean;
  canPlay: boolean;
  isLoading: boolean;
  onTogglePlay: () => void;
  /** Leaves playback. Desktop toolbar and the mobile bar both pass this. */
  onExit?: () => void;
  /** Replaces the hint when playback data or the piano sound failed. */
  statusText?: string | null;
  className?: string;
}

/**
 * Play / Pause for engraved playback mode. A tap on the score never
 * starts audio; this button is the only control that does. Pause keeps
 * the cursor where it stopped.
 */
export const ProjectPlaybackActions: React.FC<ProjectPlaybackActionsProps> = ({
  isPlaying,
  canPlay,
  isLoading,
  onTogglePlay,
  onExit,
  statusText,
  className = '',
}) => {
  let label = 'Play';
  if (isPlaying) label = 'Pause';
  const hint = statusText
    ? statusText
    : isLoading
      ? 'Loading piano sound…'
      : 'Tap a note to choose where playback starts.';
  return (
    <div
      className={`
        flex flex-1 flex-wrap items-center justify-center gap-x-3 gap-y-1
        ${className}
      `}
    >
      {onExit && (
        <ToolbarButton
          variant="ghost"
          layout="iconLabel"
          title="Leave playback"
          onClick={onExit}
        >
          <FontAwesomeIcon icon={faXmark} />
          <span>Exit</span>
        </ToolbarButton>
      )}
      <ToolbarButton
        variant="ghost"
        layout="iconLabel"
        title={
          isLoading
            ? 'Loading piano sound'
            : isPlaying
              ? 'Pause playback'
              : 'Play from the cursor'
        }
        onClick={onTogglePlay}
        disabled={!canPlay || isLoading}
        aria-pressed={isPlaying}
      >
        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        <span>{label}</span>
      </ToolbarButton>
      <p className="text-sm text-gray-500" aria-live="polite">
        {hint}
      </p>
    </div>
  );
};
